import { Config } from 'config/envConfig';
import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import Flagpage from './Flagpage';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { ChatContainer } from './chatSection';
import { alert, AlertType, ConfirmModal } from '@quyva/shared-ui';
import { NoInteractionView } from '@quyva/shared-ui';
import {
    UserAccessOptions,
    RepoType,
    ChatFormatType,
    ErrorType,
    ChartTypes,
    generateUniqueId,
    isValidJsonString,
    getCurrentUTCDateTime,
    removeUuidPrefix,
    selectionModeCode,
    sortChunks,
    SortSameDocument,
    BatchMode,
    buildSubmittedFormText,
    extractOriginalRequest,
} from '@quyva/common';
import { getUrl } from './pdfDisplay/utils';
import { ViewerContainer } from './viewerContainer';
import { useGlobalCredits } from '../context/GlobalStateContext';

// Shared by the live /qna response (handleQna) and the reloaded chat history
// (handleGetQnAHistory) - both carry the same format_type/table/list_data/
// chart_data shape, and both need the same table/list/chart display object
// so the answer keeps rendering with its detected format after a reload/tab
// switch instead of reverting to plain text.
const buildAutoFormatDisplay = (response, plainAnswer) => {
    if (response.format_type === ChatFormatType.TABLE && response.table) {
        const { header, data } = response.table;
        return {
            displayType: ChatFormatType.TABLE,
            displayAnswer: {
                tableHead: header,
                tableData: data.map((row) =>
                    Object.fromEntries(
                        header.map((col, idx) => [col, row[idx]])
                    )
                ),
            },
        };
    }
    if (response.format_type === ChatFormatType.LIST && response.list_data) {
        return {
            displayType: ChatFormatType.LIST,
            displayAnswer: { listData: response.list_data },
        };
    }
    if (response.format_type === ChatFormatType.CHART && response.chart_data) {
        return {
            displayType: ChatFormatType.CHART,
            displayAnswer: {
                columns: response.chart_data.columns,
                data: response.chart_data.data,
                chartType: response.chart_data.chart_type,
                options: {
                    title: response.chart_data.title,
                    hAxis: { title: response.chart_data.x_axis_title },
                    vAxis: { title: response.chart_data.y_axis_title },
                },
            },
        };
    }
    return { displayType: undefined, displayAnswer: plainAnswer };
};

function MainArea(props) {
    const { repo_to_chat, fine_tuned_mode, fileToChat, isAdmin, socket } =
        props;
    const { fetchUserPreferences } = useGlobalCredits();
    const [query, setQuery] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [attachmentsList, setAttachmentsList] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [qnaLoadingText, setQnaLoadingText] = useState('Generating Answer');
    const [activeQnaQid, setActiveQnaQid] = useState(null);
    const [lastQnaStatusAt, setLastQnaStatusAt] = useState(null);
    const [lastQnaBackendStatusAt, setLastQnaBackendStatusAt] = useState(null);

    const [currentqna, setcurrentqna] = useState([]);

    const [currentqnc, setcurrentqnc] = useState([]);

    const [previos_ques_at, set_previous_ques_at] = useState(0);

    const [flag_mode, set_flag_mode] = useState(false);
    const [flag_index, set_flag_index] = useState(-1);

    const [finetunelist, set_finetunelist] = useState([]);
    const user = JSON.parse(localStorage.getItem('current'));
    const [finetunedata, setfinetunedata] = useState(null);
    const [s_repo, sets_repo] = useState({});

    const [questionsData, setQuestionsData] = useState(new Map());
    const [activeQuestion, setActiveQuestion] = useState(null);
    const [fineTuningVerify, setFineTuningVerify] = useState(false);
    const [filesData, setFilesData] = useState(null);
    const [abortController, setAbortController] = useState(null);
    // Active form_input awaiting the user: { payload: <form config>, qid }. Null when none.
    const [activeForm, setActiveForm] = useState(null);

    const authToken = encodeURIComponent(user.token); // Replace with your actual authentication token

    const BREAKPOINT_WIDTH = 1024;

    const handleFetchData = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                getUrl(
                    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/getfinetune`
                ),
                {
                    params: {
                        id: user.email,
                    },
                }
            );

            set_finetunelist(response.data);
            const dataArray = response.data;
            const searchKey = 'foldername';
            const searchValue = repo_to_chat.foldername;
            const foundObject = dataArray.find(
                (obj) => obj[searchKey] === searchValue
            );
            const tvalue = foundObject ? foundObject : -1;
            setfinetunedata(tvalue);
            setIsLoading(false);
        } catch (error) {
            console.error('Error:', error);
            setIsLoading(false);
        }
    };

    const getRepoFilesData = async (repoId) => {
        try {
            setIsLoading(true);
            const dynamicUrl = getUrl(
                `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/new_alldocs/${repoId}`
            );

            const response = await axios.get(dynamicUrl);
            setFilesData(response.data);
            setIsLoading(false);
        } catch (error) {
            setIsGenerating(false);
            setIsLoading(false);
            console.log('Error in handle QNA', error);
        }
    };

    useEffect(() => {
        const handleData = async (repoid, chatonly) => {
            await getRepoFilesData(repoid);
        };

        questionsData.clear();
        setQuestionsData(questionsData);
        setcurrentqna([]);
        setcurrentqnc([]);
        setActiveQuestion(null);

        let chatonly = false;
        if (repo_to_chat.shared_type === UserAccessOptions.CHAT) {
            chatonly = true;
        }
        handleData(repo_to_chat.repoid, chatonly);
        sets_repo(repo_to_chat);
        setQuery('');
        setUploadedFiles([]);
        setAttachmentsList(null);
        setQnaLoadingText('Generating Answer');
        setActiveQnaQid(null);
        setLastQnaStatusAt(null);
    }, [fine_tuned_mode, repo_to_chat, fileToChat]);

    useEffect(() => {
        if (!socket) {
            return;
        }

        const normalizeMail = (m) => (m || '').toString().trim().toLowerCase();
        const normalizeFile = (f) => {
            if (Array.isArray(f)) {
                return f.join(',');
            }
            return (f || '').toString();
        };

        const handleQnaUpdate = (data) => {
            try {
                if (!data) {
                    return;
                }

                if (!isGenerating) {
                    return;
                }

                // Always route strictly: only apply updates to the current chat context
                // (userMail + repoId + fileToChat) and, once known, the active qid.
                const expectedUserMail = normalizeMail(user?.email);
                const incomingUserMail = normalizeMail(data.userMail);
                if (
                    expectedUserMail &&
                    incomingUserMail &&
                    expectedUserMail !== incomingUserMail
                ) {
                    return;
                }

                const expectedFile = normalizeFile(fileToChat);
                const incomingFile = normalizeFile(data.fileToChat);
                if (expectedFile !== incomingFile) {
                    return;
                }

                // Lock to the first qid we see during generation to avoid cross-window updates.
                if (isGenerating && !activeQnaQid && data.qid) {
                    setActiveQnaQid(data.qid);
                }
                if (
                    activeQnaQid &&
                    data.qid &&
                    String(activeQnaQid) !== String(data.qid)
                ) {
                    return;
                }

                if (data.message) {
                    setLastQnaBackendStatusAt(new Date().toISOString());
                    setQnaLoadingText(data.message);
                    setLastQnaStatusAt(new Date().toISOString());
                }
            } catch (e) {
                return;
            }
        };

        socket.on('syncUpdate', handleQnaUpdate);
        socket.on('asyncUpdate', handleQnaUpdate);

        return () => {
            socket.off('syncUpdate', handleQnaUpdate);
            socket.off('asyncUpdate', handleQnaUpdate);
        };
    }, [
        socket,
        isGenerating,
        repo_to_chat,
        fileToChat,
        activeQnaQid,
        user?.email,
    ]);

    const addNewFlag = (key, issue, comment) => {
        const data = questionsData.get(key);
        if (data) {
            data.issue = issue;
            data.comment = comment;
            data.flagged = true;
            questionsData.set(key, data);
        }
        setQuestionsData(questionsData);
    };

    const handleFineTune = async () => {
        if (query === '') {
            alert('empty query');
            return;
        }

        setIsLoading(true);
        const response = await fetch(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/fine_tune_chat`
            ),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: `model_id=${finetunedata.modelid}&query=${query}&email=${user.email}&foldername=${repo_to_chat.foldername}`,
            }
        );

        const data = await response.json();

        if (data.output) {
            const parsedData = JSON.parse(data.output);

            const temp_qna = {
                question: query,
                answer: parsedData.content,
                qid: parsedData.qid,
            };
            currentqna.unshift(temp_qna);
            setQuery('');

            setIsLoading(false);
        } else if (data.error) {
            alert(data.error);
            setIsLoading(false);
        }
    };

    const handleGetQnAHistory = (
        email,
        folderName,
        repoid,
        chatonly,
        fileToChat = null
    ) => {
        setIsLoading(true);
        const filePart = fileToChat
            ? `&file_to_chat=${encodeURIComponent(fileToChat)}`
            : '';
        fetch(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/get-qna-history/?email=${email}&foldername=${folderName}&repoid=${repoid}&chatonly=${chatonly}${filePart}`
            )
        )
            .then((response) => response.json())
            .then((historyData) => {
                if (historyData.detail) {
                    throw new Error(historyData.detail);
                }

                const nextQuestionsData = new Map();
                setActiveQuestion(null);
                setcurrentqna([]);
                setcurrentqnc([]);
                set_previous_ques_at(0);

                historyData
                    .sort(function (a, b) {
                        return (
                            new Date(a.metadata.timestamp) -
                            new Date(b.metadata.timestamp)
                        );
                    })
                    .forEach((data, index) => {
                        const metadata = data.metadata;
                        const response = data.response;

                        const isMultiple =
                            response.answer.includes('|END OF ANSWER|');
                        const plainAnswer = isMultiple
                            ? response.answer.split('|END OF ANSWER|')
                            : response.answer;

                        // Reconstruct the same table/list/chart presentation the
                        // live answer had, so it doesn't revert to plain text after
                        // switching tabs/repos and reloading history.
                        const { displayAnswer, displayType } =
                            isMultiple
                                ? { displayAnswer: plainAnswer, displayType: undefined }
                                : buildAutoFormatDisplay(response, plainAnswer);

                        const questionData = {
                            qid: data.qid,
                            username: metadata.user_id,
                            question: metadata.question,
                            answer: displayAnswer,
                            ...(displayType && { type: displayType }),
                            table: response.table,
                            timestamp: metadata.timestamp,
                            chunksData: response.chunks ?? [],
                            flagged: !!metadata.flag.issue,
                            issue: metadata.flag.issue,
                            comment: metadata.flag.comment,
                            fileToChat: metadata.document,
                            sqlQuery: metadata.sql_query,
                            queries: metadata.queries,
                            session_id: metadata.session_id,
                            interface: metadata.interface,
                            attachments: metadata.attachments,
                            debugInfo: data.debug_info ?? null,
                            displayRelatedDoc: true,
                        };
                        nextQuestionsData.set(data.qid, questionData);
                        if (index === historyData.length - 1) {
                            const qna = {
                                question: metadata.question,
                                answer: plainAnswer,
                                qid: data.qid,
                            };
                            setcurrentqna([qna]);
                            const qnc = {
                                question: metadata.question,
                                qid: data.qid,
                            };
                            setcurrentqnc([qnc]);
                            set_previous_ques_at(0);
                        }
                    });

                setQuestionsData(nextQuestionsData);
                setIsLoading(false);
            })
            .catch((error) => {
                alert(AlertType.ERROR, `${error.message}`);
                setIsLoading(false);
            });
    };

    const findActualFileName = (pdfName) => {
        const foundData = filesData.find((data) => {
            const index = data.name.lastIndexOf('.');
            const pdfIndex = pdfName.lastIndexOf('.');
            if (index > -1 && pdfIndex > -1) {
                if (
                    data.name.substring(0, index) ===
                    pdfName.substring(0, pdfIndex)
                )
                    return true;
            } else {
                if (data.name.substring(0, index) === pdfName) return true;
            }
            return false;
        });

        return foundData ? foundData.name : pdfName;
    };

    const handleQna = async (regenerate, query2, controller, virtualRepoId) => {
        try {
            setActiveForm(null);
            setIsGenerating(true);
            setQnaLoadingText('Searching documents and preparing context');
            setActiveQnaQid(null);
            setLastQnaStatusAt(new Date().toISOString());
            setLastQnaBackendStatusAt(null);

            const formData = new FormData();
            formData.append('repo_id', virtualRepoId || repo_to_chat.repoid);
            formData.append('query', query2);
            formData.append('user_id', user.email);
            formData.append('file_to_chat', fileToChat || '');
            formData.append('interface', 'default');
            formData.append('auto_format', 'true');
            // ⬇️ Append uploaded files (if any)
            if (uploadedFiles && uploadedFiles.length > 0) {
                uploadedFiles.forEach((file) => {
                    formData.append('uploaded_files', file); // FastAPI matches this name
                });
            }
            // console.log('FILE:', uploadedFiles);
            // Append already-attached file paths so FastAPI gets uploaded_files_path: List[str]
            if (Array.isArray(attachmentsList) && attachmentsList.length > 0) {
                attachmentsList.forEach((p) => {
                    // each path appended as uploaded_files_path
                    formData.append('uploaded_files_path', p);
                });
            }

            const qnaResponse = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/qna`
                ),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    signal: controller.signal,
                }
            );

            const parsedData = qnaResponse.data;

            if (parsedData.error) {
                throw new Error(parsedData.error);
            }

            const actualQuery = removeUuidPrefix(query2, selectionModeCode);

            if (parsedData) {
                const qid = parsedData.qid;
                const metadata = parsedData.metadata;
                const response = parsedData.response;
                const responseString = response.answer;

                // A model downgrade is reported in the bell icon by the
                // backend, not as a toast, so it survives a refresh. All that
                // is left to do here is refresh the displayed selection when
                // the sweep moved the account default.
                if (metadata?.modelMigration) {
                    const m = metadata.modelMigration;
                    if (m.user_preference_updated || m.sweep?.user_preference) {
                        fetchUserPreferences();
                    }
                }

                // Auto-detected table/list/chart presentation for a plain-language
                // answer (see auto_format on /qna). Reuses the exact answer shapes the
                // manual "Format as..." buttons already produce, so the same renderers
                // (answerContentView.jsx) apply regardless of how the format was chosen.
                // responseString stays the plain-text answer (used below for currentqna,
                // which manual reformatting reads from) - only the display copy changes.
                const { displayAnswer, displayType } = buildAutoFormatDisplay(
                    response,
                    responseString
                );

                const questionData = {
                    qid: qid,
                    question: actualQuery,
                    answer: displayAnswer,
                    ...(displayType && { type: displayType }),
                    timestamp: metadata.timestamp,
                    fileToChat: metadata.document,
                    chunksData: response.chunks ? [] : null,
                    table: response.table,
                    queries: [],
                    sqlQuery: metadata.sqlQuery,
                    selectionMode: metadata.needMoreUserInput,
                    attachments: metadata.attachments,
                    debugInfo: parsedData.debug_info ?? null,
                    displayRelatedDoc: parsedData.display_related_doc ?? true,
                };

                // Fetch queries from the second API (`/get_queries`)
                try {
                    const queriesResponse = await fetch(
                        getUrl(
                            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/get_queries?chat_id=${qid}`
                        )
                    );

                    const queriesData = await queriesResponse.json();
                    if (queriesData) {
                        // Backend may return either:
                        // - a JSON string (e.g. "[...]")
                        // - an already-parsed array/object (common)
                        if (typeof queriesData === 'string') {
                            try {
                                questionData.queries = JSON.parse(queriesData);
                            } catch (e) {
                                questionData.queries = [];
                            }
                        } else {
                            questionData.queries = queriesData;
                        }
                    }
                } catch (queriesError) {
                    console.log('Error occurred:', queriesError.message);
                    console.log('Stack Trace:', queriesError.stack);
                }

                response.chunks?.forEach((chunk) => {
                    const chunkData = {
                        email: repo_to_chat.email,
                        folderName: chunk.repositoryName,
                        fileName: chunk.document,
                        actualFileName: chunk.document,
                        pageNumber: chunk.pageNumber,
                        pageContent: chunk.content,
                        startLineNumber: chunk.startLineNumber,
                        rank: chunk.rank ?? 0,
                        associated_website: chunk.associated_website ?? null,
                    };

                    questionData.chunksData.push(chunkData);
                });

                // Sort chunks by ranked documents then page wise for same document
                const pageWiseChunks = sortChunks(
                    questionData.chunksData,
                    SortSameDocument.PAGE_WISE
                );

                questionData.chunksData = pageWiseChunks;

                questionsData.set(qid, questionData);
                setQuestionsData(questionsData);
                setActiveQuestion(questionData);

                const qna = {
                    question: actualQuery,
                    answer: responseString,
                    table: response.table,
                    qid: qid,
                };
                setcurrentqna([qna]);

                const qnc = { question: actualQuery, qid: qid };
                setcurrentqnc([qnc]);

                // A form_input tool surfaces a form for the user to fill. Show it
                // (inline or in the document panel) and block further chat input.
                const requiredInput = response.required_input;
                if (
                    requiredInput &&
                    requiredInput.type === 'form_input' &&
                    requiredInput.input_json
                ) {
                    setActiveForm({
                        payload: requiredInput.input_json,
                        qid: qid,
                        // Remembered so the submission can carry it forward - see
                        // handleFormSubmit. If this turn was itself a submission
                        // (the agent asked again), keep the request it carried
                        // instead of nesting the answers block inside it.
                        originalQuery:
                            extractOriginalRequest(actualQuery) || actualQuery,
                    });
                    // Detailed-view forms render in the document panel; auto-open it.
                    if (requiredInput.input_json.use_detailed_view) {
                        props.setOnButton?.(true);
                    }
                }

                set_previous_ques_at(0);
                setQuery('');
                setUploadedFiles([]);
                setAttachmentsList(null);
            } else if (parsedData.error || parsedData.detail) {
                alert(
                    AlertType.ERROR,
                    `${parsedData.error || parsedData.detail}`
                );
            }
        } catch (error) {
            let message = error.message;
            if (error.name === ErrorType.CANCEL_ERROR) {
                message = 'QNA Request cancelled, as repository was changed';
                // alert(AlertType.WARNING, message);
            } else {
                message = error.response?.data?.error || error.message;
                alert(AlertType.ERROR, message);
            }
        } finally {
            setIsGenerating(false);
            setActiveQnaQid(null);
            setQnaLoadingText('');
        }
    };

    const handleSubmit = async (regenerate, virtualRepoId) => {
        if (query === '' && regenerate === false) {
            alert('Question cannot be empty');
            return;
        }

        let query2 = regenerate ? currentqna[0].question : query;

        // Create a new AbortController
        const controller = new AbortController();
        setAbortController(controller); // Save it in state

        await handleQna(regenerate, query2, controller, virtualRepoId);

        return;
    };

    // Effect to handle repository change
    useEffect(() => {
        if (abortController) {
            // Abort the previous request
            abortController.abort();
        }
        // Clear the controller after aborting
        setAbortController(null);
    }, [repo_to_chat.repoid]); // Run this effect whenever repo changes

    const handleRegenerate = async (e) => {
        e.preventDefault();
        setIsGenerating(true);
        await getRepoFilesData(repo_to_chat.repoid);
        const response = await fetch(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/regenerate`
            ),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    email: repo_to_chat.email,
                    foldername: repo_to_chat.foldername,
                    query: currentqna[previos_ques_at].question,
                }).toString(),
            }
        );

        const data = await response.json();
        if (data.output) {
            const parsedData = JSON.parse(data.output);
            const docs2 = JSON.parse(parsedData.docs);
            const response2 = parsedData.response;
            const qid = parsedData.qid;

            const questionData = {
                qid: qid,
                question: currentqna[previos_ques_at].question,
                answer: response2,
                timestamp: getCurrentUTCDateTime(),
                chunksData: [],
                fileToChat: parsedData.file_to_chat,
            };

            docs2.forEach((pageContent, index) => {
                const chunkData = {
                    email: repo_to_chat.email,
                    folderName: repo_to_chat.foldername,
                    fileName: parsedData.metadata[index].pdf_name,
                    actualFileName: findActualFileName(
                        parsedData.metadata[index].pdf_name
                    ),
                    pageNumber: parsedData.metadata[index].page_number,
                    pageContent: pageContent.page_content,
                    ...(parsedData.metadata[index].start_line_number !==
                        undefined && {
                        startLineNumber:
                            parsedData.metadata[index].start_line_number,
                    }),
                    ...(parsedData.metadata[index].end_line_number !==
                        undefined && {
                        endLineNumber:
                            parsedData.metadata[index].end_line_number,
                    }),
                };
                questionData.chunksData.push(chunkData);
            });

            questionsData.set(qid, questionData);
            setQuestionsData(questionsData);

            setActiveQuestion(questionData);

            const temp_qna = {
                question: currentqna[previos_ques_at].question,
                answer: response2,
                qid: qid,
            };
            currentqna.unshift(temp_qna);
            const temp_qnc = {
                question: currentqna[previos_ques_at].question,
                docs: docs2,
                qid: qid,
            };
            currentqnc.unshift(temp_qnc);
            set_previous_ques_at(0);
            setQuery('');

            setIsGenerating(false);
        } else if (data.error || data.detail) {
            setIsGenerating(false);
            alert(`Error: ${data.error || data.detail}`);
        }
    };

    const handleChartFormat = async (chatData, isDatabase, chartType) => {
        try {
            let answer = chatData.answer;
            if (isValidJsonString(answer)) {
                answer = JSON.parse(answer);
            } else {
                answer = chatData.answer;
            }

            if (
                answer.type === ChatFormatType.TABLE &&
                (isDatabase === RepoType.SQLLITE ||
                    isDatabase === RepoType.POSTGRESS ||
                    isDatabase === RepoType.MSSQL ||
                    isDatabase === RepoType.TABULAR)
            ) {
                const payload = answer.payload;
                const columnData = payload.columns;
                const data = payload.data;

                const chartOptions = {
                    title: `${columnData[0]} vs ${columnData[1]} chart`,
                    hAxis: {
                        title: columnData[0],
                    },
                    vAxis: {
                        title: columnData[1],
                    },
                };

                const defaultChart = chartType
                    ? chartType
                    : ChartTypes.BAR_CHART;

                const chartData = {
                    columns: columnData,
                    data: data,
                    chartType: defaultChart,
                    options: chartOptions,
                };

                const qid = generateUniqueId();
                const questionData = {
                    qid: qid,
                    originalQid: currentqna[previos_ques_at]?.qid,
                    question: currentqna[previos_ques_at].question,
                    answer: chartData,
                    timestamp: getCurrentUTCDateTime(),
                    chunksData: [],
                    type: ChatFormatType.CHART,
                    fileToChat: null,
                };

                questionsData.set(qid, questionData);
                setQuestionsData(questionsData);
                setActiveQuestion(questionData);
            } else {
                setIsGenerating(true);

                const ques = `${currentqna[previos_ques_at]?.question}`;
                const chunk = `${currentqna[previos_ques_at]?.answer}`;

                const response = await fetch(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/formatans`
                    ),
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                            email: repo_to_chat.email,
                            foldername: repo_to_chat.name,
                            chat_format_type: ChatFormatType.CHART,
                            ques: ques,
                            chunk: chunk,
                            fileToChat: fileToChat,
                        }).toString(),
                    }
                );

                const data = await response.json();
                if (data.output) {
                    const parsedData = JSON.parse(data.output);
                    const response2 = JSON.parse(parsedData.response);
                    const qid = parsedData.qid;
                    const docs2 = parsedData.docs
                        ? JSON.parse(parsedData.docs)
                        : [];

                    const previousQid = currentqna[previos_ques_at]?.qid;
                    const chunksData =
                        questionsData.get(previousQid).chunksData;
                    const questionData = {
                        qid: qid,
                        originalQid: previousQid,
                        question: currentqna[previos_ques_at].question,
                        answer: response2,
                        timestamp: getCurrentUTCDateTime(),
                        chunksData: chunksData,
                        type: ChatFormatType.CHART,
                        fileToChat: parsedData.fileToChat,
                        displayRelatedDoc: false,
                    };
                    docs2.forEach((pageContent, index) => {
                        const chunkData = {
                            email: repo_to_chat.email,
                            folderName: repo_to_chat.name,
                            fileName: parsedData.metadata[index].pdf_name,
                            actualFileName: findActualFileName(
                                parsedData.metadata[index].pdf_name
                            ),
                            pageNumber: parsedData.metadata[index].page_number,
                            pageContent: pageContent.page_content,
                            ...(parsedData.metadata[index].start_line_number !==
                                undefined && {
                                startLineNumber:
                                    parsedData.metadata[index]
                                        .start_line_number,
                            }),
                            ...(parsedData.metadata[index].end_line_number !==
                                undefined && {
                                endLineNumber:
                                    parsedData.metadata[index].end_line_number,
                            }),
                        };
                        questionData.chunksData.push(chunkData);
                    });

                    questionsData.set(qid, questionData);
                    setQuestionsData(questionsData);

                    setActiveQuestion(questionData);
                    setQuery('');
                } else if (data.error || data.detail) {
                    throw new Error(data.error || data.detail);
                }
            }
        } catch (error) {
            alert(error.message);
        } finally {
            setTimeout(() => {
                setIsGenerating(false);
            }, 100);
        }
    };

    const handleTableFormat = async (e) => {
        if (e) {
            e.preventDefault();
        }

        setIsGenerating(true);
        const ques = `${currentqna[previos_ques_at]?.question} `;
        const chunk = `${currentqna[previos_ques_at]?.answer}`;

        const response = await fetch(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/formatans`
            ),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    email: repo_to_chat.email,
                    foldername: repo_to_chat.name,
                    chat_format_type: ChatFormatType.TABLE,
                    ques: ques,
                    chunk: chunk,
                    file_to_chat: fileToChat,
                }).toString(),
            }
        );

        const data = await response.json();
        if (data.output) {
            const parsedData = JSON.parse(data.output);
            const response2 = JSON.parse(parsedData.response);
            const qid = parsedData.qid;
            const docs2 = parsedData.docs ? JSON.parse(parsedData.docs) : [];

            const previousQid = currentqna[previos_ques_at]?.qid;
            const chunksData = questionsData.get(previousQid).chunksData;
            const questionData = {
                qid: qid,
                originalQid: previousQid,
                question: currentqna[previos_ques_at].question,
                answer: response2,
                timestamp: getCurrentUTCDateTime(),
                chunksData: chunksData,
                type: ChatFormatType.TABLE,
                fileToChat: parsedData.file_to_chat,
                displayRelatedDoc: false,
            };
            docs2.forEach((pageContent, index) => {
                const chunkData = {
                    email: repo_to_chat.email,
                    folderName: repo_to_chat.name,
                    fileName: parsedData.metadata[index].pdf_name,
                    actualFileName: findActualFileName(
                        parsedData.metadata[index].pdf_name
                    ),
                    pageNumber: parsedData.metadata[index].page_number,
                    pageContent: pageContent.page_content,
                    pageContent: pageContent.page_content,
                    ...(parsedData.metadata[index].start_line_number !==
                        undefined && {
                        startLineNumber:
                            parsedData.metadata[index].start_line_number,
                    }),
                    ...(parsedData.metadata[index].end_line_number !==
                        undefined && {
                        endLineNumber:
                            parsedData.metadata[index].end_line_number,
                    }),
                };
                questionData.chunksData.push(chunkData);
            });

            questionsData.set(qid, questionData);
            setQuestionsData(questionsData);

            setActiveQuestion(questionData);

            setQuery('');

            setIsGenerating(false);
        } else if (data.error || data.detail) {
            setIsGenerating(false);
            alert(`Error: ${data.error || data.detail}`);
        }
    };

    const handleListFormat = async (e) => {
        if (e) {
            e.preventDefault();
        }

        setIsGenerating(true);

        const ques = `${currentqna[previos_ques_at]?.question} `;
        const chunk = `${currentqna[previos_ques_at]?.answer}`;

        const response = await fetch(
            getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/formatans`
            ),
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    email: repo_to_chat.email,
                    foldername: repo_to_chat.name,
                    chat_format_type: ChatFormatType.LIST,
                    ques: ques,
                    chunk: chunk,
                    file_to_chat: fileToChat,
                }).toString(),
            }
        );

        const data = await response.json();
        if (data.output) {
            const parsedData = JSON.parse(data.output);

            const response2 = JSON.parse(parsedData.response);
            const qid = parsedData.qid;
            const docs2 = parsedData.docs ? JSON.parse(parsedData.docs) : [];

            const previousQid = currentqna[previos_ques_at]?.qid;
            const chunksData = questionsData.get(previousQid).chunksData;

            const questionData = {
                qid: qid,
                originalQid: previousQid,
                question: currentqna[previos_ques_at].question,
                answer: response2,
                timestamp: getCurrentUTCDateTime(),
                chunksData: chunksData,
                type: ChatFormatType.LIST,
                fileToChat: parsedData.file_to_chat,
                displayRelatedDoc: false,
            };

            docs2.forEach((pageContent, index) => {
                const chunkData = {
                    email: repo_to_chat.email,
                    folderName: repo_to_chat.name,
                    fileName: parsedData.metadata[index].pdf_name,
                    actualFileName: findActualFileName(
                        parsedData.metadata[index].pdf_name
                    ),
                    pageNumber: parsedData.metadata[index].page_number,
                    pageContent: pageContent.page_content,
                    pageContent: pageContent.page_content,
                    ...(parsedData.metadata[index].start_line_number !==
                        undefined && {
                        startLineNumber:
                            parsedData.metadata[index].start_line_number,
                    }),
                    ...(parsedData.metadata[index].end_line_number !==
                        undefined && {
                        endLineNumber:
                            parsedData.metadata[index].end_line_number,
                    }),
                };
                questionData.chunksData.push(chunkData);
            });

            questionsData.set(qid, questionData);
            setQuestionsData(questionsData);

            setActiveQuestion(questionData);

            setQuery('');

            setIsGenerating(false);
        } else if (data.error || data.detail) {
            setIsGenerating(false);

            alert(`Error: ${data.error || data.detail}`);
        }
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
            // Reset togglePdfVisibility here
            props.togglePdfVisibility();
        };

        // Add event listener for beforeunload event
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []); // Empty dependency array to ensure this effect runs only once on mount

    useEffect(() => {
        // Reset both states to false when the component mounts or updates
        props.setShowPdf(false);
        props.setOnButton(false);
    }, []);

    // Document data present
    let hasValidDocumentChunks = false;
    const hasChunksData =
        activeQuestion &&
        activeQuestion.chunksData &&
        activeQuestion.chunksData.length != 0;

    if (hasChunksData) {
        for (let index = 0; index < activeQuestion.chunksData.length; index++) {
            const chunk = activeQuestion.chunksData[index];
            if (chunk.fileName) {
                hasValidDocumentChunks = true;
                break;
            }
        }
    }

    // Check if all chunks are from URL-based markdown files (have associated_website)
    const areAllChunksFromUrls =
        hasChunksData &&
        activeQuestion.chunksData.every((chunk) => chunk.associated_website);

    // form_input rendering: detailed-view forms go to the document panel; others inline in chat.
    const formPending = !!activeForm;
    const formDetailed = !!activeForm?.payload?.use_detailed_view;
    const showFormInPanel = formPending && formDetailed;
    const inlineForm = formPending && !formDetailed ? activeForm.payload : null;

    const handleFormSubmit = async (answers) => {
        // Send the collected answers as the next /qna turn. The text format is a
        // contract with the backend's form_input parser - see buildSubmittedFormText.
        // The request that opened the form is appended AFTER the answers so the agent
        // router and any classifier step still see what the user actually asked for,
        // while the message keeps starting with the submitted-form marker the backend
        // guards on.
        const submissionText = buildSubmittedFormText(
            answers,
            activeForm?.originalQuery
        );
        setActiveForm(null);
        const controller = new AbortController();
        setAbortController(controller);
        await handleQna(false, submissionText, controller);
    };

    const handleFormCancel = () => {
        setActiveForm(null);
    };

    const displayDocumentContainer =
        (hasChunksData && activeQuestion.displayRelatedDoc !== false) ||
        showFormInPanel;

    return (
        <div className='h-full w-full bg-backgrounds-secondary'>
            {isLoading && (
                        <NoInteractionView viewText={'Loading ...'} />
                    )}
                    <div className='flex items-center justify-center px-0 z-0 h-[86vh]'>
                        <div className='w-full h-full'>
                            {displayDocumentContainer ? (
                                <ReflexContainer orientation='vertical'>
                                    {!props.showPdf && (
                                        <ReflexElement className='left-pane'>
                                            <div className='pane-content pr-0.5 w-full h-full bg-transparent'>
                                                <div className='w-full h-full border border-1 overflow-auto bg-backgrounds-secondary rounded-xl'>
                                                    <ChatContainer
                                                        activeQuestion={
                                                            activeQuestion
                                                        }
                                                        isGenerating={
                                                            isGenerating
                                                        }
                                                        qnaLoadingText={
                                                            qnaLoadingText
                                                        }
                                                        setIsGenerating={
                                                            setIsGenerating
                                                        }
                                                        questionsData={
                                                            questionsData
                                                        }
                                                        flagMode={flag_mode}
                                                        query={query}
                                                        currentQnA={currentqna}
                                                        fineTunedMode={
                                                            fine_tuned_mode
                                                        }
                                                        fileToChat={fileToChat}
                                                        repository={repo_to_chat}
                                                        repoName={
                                                            repo_to_chat.name
                                                        }
                                                        user={user}
                                                        setFlagMode={
                                                            set_flag_mode
                                                        }
                                                        setFlagIndex={
                                                            set_flag_index
                                                        }
                                                        setQuery={setQuery}
                                                        handleChartFormat={
                                                            handleChartFormat
                                                        }
                                                        handleTableFormat={
                                                            handleTableFormat
                                                        }
                                                        handleListFormat={
                                                            handleListFormat
                                                        }
                                                        // handleCustomFormat={
                                                        //     handleCustomFormat
                                                        // }
                                                        handleFineTune={
                                                            handleFineTune
                                                        }
                                                        handleSubmit={
                                                            handleSubmit
                                                        }
                                                        handleGetQnAHistory={
                                                            handleGetQnAHistory
                                                        }
                                                        handleRegenerate={
                                                            handleRegenerate
                                                        }
                                                        setFineTuningVerify={
                                                            setFineTuningVerify
                                                        }
                                                        isAdmin={isAdmin}
                                                        isDatabase={
                                                            repo_to_chat.repotype
                                                        }
                                                        togglePdfVisibility={
                                                            props.togglePdfVisibility
                                                        }
                                                        setActiveQuestion={
                                                            setActiveQuestion
                                                        }
                                                        setQuestionsData={
                                                            setQuestionsData
                                                        }
                                                        setcurrentqna={
                                                            setcurrentqna
                                                        }
                                                        setcurrentqnc={
                                                            setcurrentqnc
                                                        }
                                                        processingRepos={
                                                            props.processingRepos
                                                        }
                                                        repoId={
                                                            repo_to_chat.repoid
                                                        }
                                                        uploadedFiles={
                                                            uploadedFiles
                                                        }
                                                        setUploadedFiles={
                                                            setUploadedFiles
                                                        }
                                                        attachmentsList={
                                                            attachmentsList
                                                        }
                                                        setAttachmentsList={
                                                            setAttachmentsList
                                                        }
                                                        chatForm={inlineForm}
                                                        formPending={
                                                            formPending
                                                        }
                                                        onFormSubmit={
                                                            handleFormSubmit
                                                        }
                                                        onFormCancel={
                                                            handleFormCancel
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </ReflexElement>
                                    )}

                                    {!props.isSmallScreen && (
                                        <ReflexSplitter
                                            style={{
                                                border: 0,
                                                background: 'transparent',
                                                width: '6px',
                                                zIndex: 0,
                                            }}
                                        />
                                    )}

                                    {/* Render PDF view based on showPdf (or a detailed-view form) */}
                                    {(showFormInPanel ||
                                        props.showPdf ||
                                        props.onButton ||
                                        window.innerWidth >
                                            BREAKPOINT_WIDTH) && (
                                        <ReflexElement className='right-pane'>
                                            <div className='pane-content pl-0.5 w-full h-full bg-transparent'>
                                                <div className='w-full h-full border border-1 overflow-auto bg-backgrounds-secondary rounded-xl'>
                                                    <ViewerContainer
                                                        activeQuestion={
                                                            activeQuestion
                                                        }
                                                        toggleVisibility={
                                                            props.togglePdfVisibility
                                                        }
                                                        isSmallScreen={
                                                            props.isSmallScreen
                                                        }
                                                        formInPanel={
                                                            showFormInPanel
                                                                ? activeForm.payload
                                                                : null
                                                        }
                                                        onFormSubmit={
                                                            handleFormSubmit
                                                        }
                                                        onFormCancel={
                                                            handleFormCancel
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </ReflexElement>
                                    )}
                                </ReflexContainer>
                            ) : (
                                <ReflexContainer orientation='vertical'>
                                    <ReflexElement className='middle-pane'>
                                        <div className='pane-content w-full h-full bg-transparent'>
                                            <div className='w-full h-full border border-1 overflow-auto bg-backgrounds-secondary rounded-xl'>
                                                <ChatContainer
                                                    activeQuestion={
                                                        activeQuestion
                                                    }
                                                    fileToChat={fileToChat}
                                                    isGenerating={isGenerating}
                                                    qnaLoadingText={
                                                        qnaLoadingText
                                                    }
                                                    setIsGenerating={
                                                        setIsGenerating
                                                    }
                                                    questionsData={
                                                        questionsData
                                                    }
                                                    flagMode={flag_mode}
                                                    query={query}
                                                    currentQnA={currentqna}
                                                    fineTunedMode={
                                                        fine_tuned_mode
                                                    }
                                                    repository={repo_to_chat}
                                                    repoName={repo_to_chat.name}
                                                    user={user}
                                                    setFlagMode={set_flag_mode}
                                                    setFlagIndex={
                                                        set_flag_index
                                                    }
                                                    setQuery={setQuery}
                                                    handleChartFormat={
                                                        handleChartFormat
                                                    }
                                                    handleTableFormat={
                                                        handleTableFormat
                                                    }
                                                    handleListFormat={
                                                        handleListFormat
                                                    }
                                                    // handleCustomFormat={
                                                    //     handleCustomFormat
                                                    // }
                                                    handleFineTune={
                                                        handleFineTune
                                                    }
                                                    handleSubmit={handleSubmit}
                                                    handleGetQnAHistory={
                                                        handleGetQnAHistory
                                                    }
                                                    handleRegenerate={
                                                        handleRegenerate
                                                    }
                                                    setFineTuningVerify={
                                                        setFineTuningVerify
                                                    }
                                                    isAdmin={isAdmin}
                                                    isDatabase={
                                                        repo_to_chat.repotype
                                                    }
                                                    togglePdfVisibility={
                                                        props.togglePdfVisibility
                                                    }
                                                    processingRepos={
                                                        props.processingRepos
                                                    }
                                                    repoId={repo_to_chat.repoid}
                                                    uploadedFiles={
                                                        uploadedFiles
                                                    }
                                                    setUploadedFiles={
                                                        setUploadedFiles
                                                    }
                                                    attachmentsList={
                                                        attachmentsList
                                                    }
                                                    setAttachmentsList={
                                                        setAttachmentsList
                                                    }
                                                    chatForm={inlineForm}
                                                    formPending={formPending}
                                                    onFormSubmit={
                                                        handleFormSubmit
                                                    }
                                                    onFormCancel={
                                                        handleFormCancel
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </ReflexElement>
                                </ReflexContainer>
                            )}
                        </div>
                    </div>

            {fineTuningVerify && (
                <ConfirmModal
                    modalText={'Start fine tuning ?'}
                    onConfirm={() => {
                        alert('Feature is under development. Get back soon!!');
                        setFineTuningVerify(false);
                    }}
                    onCancel={() => setFineTuningVerify(false)}
                />
            )}

            {flag_mode && (
                <Flagpage
                    questionsData={questionsData}
                    setflag={set_flag_mode}
                    add_flag={addNewFlag}
                    query={questionsData.get(flag_index).question}
                    response={
                        (() => {
                            const ans = questionsData.get(flag_index).answer;
                            return typeof ans === 'string' ? ans : JSON.stringify(ans);
                        })()
                    }
                    email={repo_to_chat.email}
                    foldername={repo_to_chat.foldername}
                    qid={
                        questionsData.get(flag_index).originalQid ||
                        flag_index
                    }
                    formatType={
                        [
                            ChatFormatType.TABLE,
                            ChatFormatType.CHART,
                            ChatFormatType.LIST,
                        ].includes(questionsData.get(flag_index).type)
                            ? questionsData.get(flag_index).type
                            : ''
                    }
                />
            )}
        </div>
    );
}

export default MainArea;
